import React, { useContext, useState, useEffect, useMemo } from 'react';
import { injectIntl } from 'react-intl';
import { useSelector, useDispatch } from 'react-redux';
import { makeStyles, Grid, CircularProgress } from '@material-ui/core';

import { Field } from 'formik';

import { FormServiceContext } from 'app/utils/contexts';
import { fecthAllRevisions } from 'app/store/slices/revisions';
import { handleChangeFile, handleDeleteFile } from 'app/utils/forms/files';
import { clearFiles, setFiles } from 'app/store/slices/forms';

import { SERVICES_CODES } from 'app/config/services';
import { DOCUMENT_TYPES } from 'app/utils/constants';
import { STATUS_CODES } from 'app/config/status';

import DocumentField from '../DocumentField';
import StickMessage from '../StickMessage';
import OtherDocuments from '../OtherDocuments';
import LicenseVehicleDocuments from './components/LicenseVehicleDocuments';

import styles from './styles';
const useStyles = makeStyles(styles);

function Documentation(props) {
    const classes = useStyles();
    const dispatch = useDispatch();

    const { intl, values, filterDocuments = (e) => e } = props;

    const { canEdit } = useContext(FormServiceContext);

    const service = useSelector((state) => state.services.details);
    const documentsStore = useSelector((state) => state.services.documents);
    const filesStore = useSelector((state) => state.forms.files);
    const request = useSelector((state) => state.requests.details);
    const revisions = useSelector((state) => state.revisions.revisions);

    const [loading, setLoading] = useState(true);
    const [loadingVehicleDocs, setLoadingVehicleDocs] = useState(false);

    const [uploading, setUpLoading] = useState(false);
    const [othersDocuments, setOthersDocuments] = useState([]);
    const [loadingDocuments, setLoadingDocuments] = useState(false);

    const hideOtherDocuments = useMemo(() => {
        if (
            service.cdServico === SERVICES_CODES.CHANGE_EMAIL ||
            service.cdServico === SERVICES_CODES.ACESSO_PORTAL_FISICA ||
            service.cdServico === SERVICES_CODES.ACESSO_PORTAL_JURIDICA
        ) {
            return true;
        }
        return othersDocuments.length === 0 && !canEdit;
    }, [canEdit, othersDocuments.length, service.cdServico]);

    useEffect(() => {
        return () => {
            dispatch(clearFiles());
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const init = () => {
            if (values?.documentos) {
                const temp = values.documentos
                    .filter((doc) => {
                        return doc.cdTipoDocumento === DOCUMENT_TYPES.OTHERS;
                    })
                    .map((doc) => {
                        const t = { ...doc };
                        t.canEdit = canEdit;
                        if (revisions && Object.keys(revisions).length > 0) {
                            let lastKey = Object.keys(revisions).pop();
                            const revision = revisions[lastKey];
                            if (revision?.documentos && revision?.documentos.length > 0) {
                                const thisDocument = revision.documentos.find((d) => d.idFile === doc.idFile);
                                if (thisDocument?.boConformidade) {
                                    t.canEdit = false;
                                    if (!t.deDescricao || t.deDescricao === '') {
                                        t.deDescricao =
                                            t.nmTipoDocumento && t.nmTipoDocumento !== ''
                                                ? t.nmTipoDocumento
                                                : t.fileName && t.fileName !== ''
                                                  ? t.fileName.split('.').slice(0, -1).join('')
                                                  : '';
                                    }
                                }
                            }
                        }
                        return t;
                    });
                setOthersDocuments(temp);
                dispatch(setFiles(temp));
            }
        };
        init();
    }, [canEdit, dispatch, revisions, values]);

    useEffect(() => {
        const init = () => {
            const aditionalDocs = filesStore.filter((doc) => {
                return doc.cdTipoDocumento === DOCUMENT_TYPES.OTHERS;
            });
            if (!values?.documentos) {
                values.documentos = [];
            }
            let formDocuments = [...values.documentos];
            if (aditionalDocs.length > 0) {
                aditionalDocs.forEach((doc) => {
                    const existingIndex = formDocuments.findIndex((o) => o.idFile === doc.idFile);
                    if (existingIndex >= 0) {
                        formDocuments[existingIndex] = { ...formDocuments[existingIndex], ...doc };
                    } else {
                        formDocuments.push({ ...doc });
                    }
                });
                values.documentos = formDocuments;
            }
        };
        init();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filesStore]);

    useEffect(() => {
        (async () => {
            setLoading(true);

            if (request.cdSituacao !== STATUS_CODES.EM_CONSTRUCAO && request.cdSolicitacao) {
                if (Object.keys(revisions).length === 0) {
                    await dispatch(fecthAllRevisions(request.cdSolicitacao));
                }
            }

            setLoading(false);
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, request]);

    const [canEditDocument] = useState(() => {
        if (!canEdit) {
            if (
                service.cdServico === SERVICES_CODES.INATIVACAO_CADASTRAL ||
                service.cdServico === SERVICES_CODES.EXCLUSAO_VEICULOS
            ) {
                if (!request.situacao) return true; // new request
                return request.situacao?.boEdicaoPortalExterno || request.cdSituacao === STATUS_CODES.EM_CONSTRUCAO;
            }
        }
        return canEdit;
    });

    if (loading) {
        return (
            <Grid container spacing={3} className={classes.documentsContainer}>
                <Grid item xs={12}>
                    <div className={classes.centerProgress}>
                        <CircularProgress size={12} />
                    </div>
                </Grid>
            </Grid>
        );
    }

    function getDocumentsList() {
        return documentsStore.filter(filterDocuments).map((doc, index) => {
            let canEditThisDoc = canEditDocument;
            if (revisions && Object.keys(revisions).length > 0) {
                const lastRevision = revisions[Object.keys(revisions).pop()];
                if (lastRevision?.documentos && lastRevision.documentos.length > 0) {
                    const thisDocument = lastRevision.documentos.find((d) => d.cdTipoDocumento === doc.cdTipoDocumento);
                    if (thisDocument?.boConformidade) {
                        canEditThisDoc = false;
                    }
                }
            }

            let idFile = null;
            let idGdoc = null;
            let value = null;
            if (values && values.documentos && values.documentos.length > 0) {
                const docValues = values.documentos.find((d) => d.cdTipoDocumento === doc.cdTipoDocumento);
                if (docValues) {
                    idFile = docValues.idFile;
                    idGdoc = docValues.idGdoc;
                    value = docValues.fileName;
                }
            }

            return (
                <Grid
                    key={`${doc.cdTipoDocumento}-${index}`}
                    item
                    xs={12}
                    sm={6}
                    md={4}
                    className={classes.documentsItens}
                    style={{ order: `${index}` }}>
                    <Field
                        value={value}
                        required={doc.boDocumentoObrigatorio}
                        name={doc.sgTipoDocumento ? doc.sgTipoDocumento : `doc-${doc.cdTipoDocumento}`}
                        label={doc.nmTipoDocumento}
                        id={doc.cdTipoDocumento}
                        disabled={!canEditThisDoc}
                        idGdoc={idGdoc}
                        idFile={idFile}
                        onChangeFile={(docResult) => {
                            handleChangeFile(docResult, doc.cdTipoDocumento, values);
                            setUpLoading(false);
                        }}
                        onDeleteFile={(idFile) => handleDeleteFile(idFile, values)}
                        uploading={uploading || loadingVehicleDocs}
                        setUpLoading={setUpLoading}
                        guest={[
                            SERVICES_CODES.CHANGE_EMAIL,
                            SERVICES_CODES.ACESSO_PORTAL_JURIDICA,
                            SERVICES_CODES.ACESSO_PORTAL_FISICA,
                        ].includes(service.cdServico)}
                        component={DocumentField}
                    />
                </Grid>
            );
        });
    }

    return (
        <>
            <Grid container spacing={3} className={classes.documentsContainer}>
                {getDocumentsList()}
                {documentsStore.length > 0 && (
                    <Grid item xs={12} sm={6} md={4} className={classes.stickMessageContainer}>
                        <div className={classes.stickMessage}>
                            <StickMessage
                                type="alert"
                                title={intl.formatMessage({ id: 'MESSAGES.FORMS.IMPORTANT' })}
                                subtitle={intl.formatMessage({
                                    id: 'MESSAGES.FORMS.VERIFY_DOCUMENTS_REQUIRED',
                                })}
                            />
                        </div>
                    </Grid>
                )}
            </Grid>

            <LicenseVehicleDocuments
                {...props}
                classes={classes}
                canEdit={canEdit}
                loadingVehicleDocs={loadingVehicleDocs}
                setLoadingVehicleDocs={setLoadingVehicleDocs}
            />

            {!hideOtherDocuments && (
                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <OtherDocuments
                            {...props}
                            type={'general'}
                            othersDocuments={othersDocuments}
                            setOthersDocuments={setOthersDocuments}
                            canEdit={canEdit}
                            disableEditFile={!canEdit}
                            loadingDocuments={loadingDocuments}
                            setLoadingDocuments={setLoadingDocuments}
                        />
                    </Grid>
                </Grid>
            )}
        </>
    );
}

export default injectIntl(Documentation);
